import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import appLogo from '../assets/logo.svg';

export function ForgotPasswordPage() {
  const { resetPasswordForEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const clean = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setError('Email invalide');
      return;
    }
    setSubmitting(true);
    const { error: err } = await resetPasswordForEmail(clean);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src={appLogo} alt="Logo" className="h-16 w-auto object-contain mb-4" />
          <h1 className="text-xl font-bold text-foreground">Prévisionnel Financier</h1>
          <p className="text-sm text-muted-foreground mt-1">My Notary — Outil interne</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mot de passe oublié</CardTitle>
            <CardDescription>
              {sent
                ? 'Un email vous a été envoyé avec un lien pour réinitialiser votre mot de passe.'
                : 'Entrez votre adresse email. Nous vous enverrons un lien pour définir un nouveau mot de passe.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-3">
                  <Icon icon="lucide:mail-check" className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    Vérifiez votre boîte mail <span className="font-medium">{email}</span>. Le lien expire dans 1 heure.
                  </div>
                </div>
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">
                    <Icon icon="lucide:arrow-left" className="w-4 h-4" />
                    Retour à la connexion
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    disabled={submitting}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-danger bg-danger/5 border border-danger/20 rounded-md px-3 py-2">
                    <Icon icon="lucide:alert-circle" className="w-4 h-4 shrink-0" />
                    {error}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Icon icon="lucide:loader-2" className="w-4 h-4 animate-spin" />
                      Envoi…
                    </>
                  ) : (
                    <>
                      <Icon icon="lucide:send" className="w-4 h-4" />
                      Envoyer le lien
                    </>
                  )}
                </Button>

                <Link
                  to="/login"
                  className="block text-center text-xs text-muted-foreground hover:text-foreground"
                >
                  Retour à la connexion
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
